
import { useState } from 'react';

const FormSection = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    nomEntreprise: '',
    secteurActivite: '',
    tailleEntreprise: '',
    offre: '',
    valeurAjoutee: '',
    besoins: '',
    objectifs: '',
    clienteleCible: '',
    zoneGeographique: '',
    email: '',
    telephone: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d5oerl00pjq8qvj95g40', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nomEntreprise: '',
          secteurActivite: '',
          tailleEntreprise: '',
          offre: '',
          valeurAjoutee: '',
          besoins: '',
          objectifs: '',
          clienteleCible: '',
          zoneGeographique: '',
          email: '',
          telephone: '',
        });
        setCurrentStep(1);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Informations entreprise' },
    { number: 2, title: 'Votre offre' },
    { number: 3, title: 'Vos besoins' },
    { number: 4, title: 'Clientèle cible' },
  ];

  return (
    <section className="py-20 md:py-32 bg-gray-light/5">
      <div className="max-w-[1400px] mx-auto px-8">
        {/* Hero */}
        <div className="text-center mb-12 md:mb-16">
          <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-5 md:mb-6 bg-primary-light/20 rounded-2xl">
            <i className="ri-links-line text-4xl md:text-5xl text-primary-light"></i>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-dark mb-4 md:mb-6">
            Rejoignez le Réseau Local
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-gray-dark max-w-2xl mx-auto leading-relaxed">
            Remplissez ce formulaire pour nous faire part de votre activité et de vos besoins. Nous vous recontacterons pour vous indiquer les opportunités disponibles dans votre agglomération.
          </p>
        </div>

        {/* Form Container */}
        <div className="max-w-[800px] mx-auto bg-white rounded-3xl shadow-xl p-6 md:p-12">
          {/* Progress Bar */}
          <div className="mb-8 md:mb-12">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full font-bold text-xs md:text-sm transition-all ${
                        currentStep >= step.number
                          ? 'bg-primary text-white'
                          : 'bg-gray-light text-gray-dark'
                      }`}
                    >
                      {step.number}
                    </div>
                    <span className="text-xs text-gray-dark mt-2 text-center hidden md:block">
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 transition-all ${
                        currentStep > step.number ? 'bg-primary' : 'bg-gray-light'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} data-readdy-form id="formulaire-professionnels">
            {/* Step 1 */}
            {currentStep === 1 && (
              <div className="space-y-5 md:space-y-6">
                <h2 className="text-xl md:text-2xl font-bold text-primary-dark mb-4 md:mb-6">
                  Informations sur votre entreprise
                </h2>
                
                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-dark mb-2">
                    Nom de l'entreprise *
                  </label>
                  <input
                    type="text"
                    name="nomEntreprise"
                    value={formData.nomEntreprise}
                    onChange={handleInputChange}
                    required
                    className="w-full border-2 border-gray-light rounded-xl px-4 py-2.5 md:py-3 text-sm md:text-base focus:border-primary focus:outline-none transition-colors"
                    placeholder="Votre raison sociale"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-dark mb-2">
                    Secteur d'activité *
                  </label>
                  <select
                    name="secteurActivite"
                    value={formData.secteurActivite}
                    onChange={handleInputChange}
                    required
                    className="w-full border-2 border-gray-light rounded-xl px-4 py-2.5 md:py-3 text-sm md:text-base focus:border-primary focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Sélectionnez votre secteur</option>
                    <option value="artisanat">Artisanat</option>
                    <option value="commerce">Commerce</option>
                    <option value="services">Services</option>
                    <option value="industrie">Industrie</option>
                    <option value="batiment">Bâtiment</option>
                    <option value="sante">Santé</option>
                    <option value="liberal">Profession libérale</option>
                    <option value="numerique">Numérique</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-dark mb-2">
                    Taille de l'entreprise *
                  </label>
                  <select
                    name="tailleEntreprise"
                    value={formData.tailleEntreprise}
                    onChange={handleInputChange}
                    required
                    className="w-full border-2 border-gray-light rounded-xl px-4 py-2.5 md:py-3 text-sm md:text-base focus:border-primary focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Sélectionnez la taille</option>
                    <option value="micro">Micro-entrepreneur</option>
                    <option value="tpe">TPE (1-9 salariés)</option>
                    <option value="pme">PME (10-249 salariés)</option>
                    <option value="eti">ETI (250+ salariés)</option>
                  </select>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="bg-primary text-white px-6 md:px-8 py-2.5 md:py-3 rounded-xl text-sm md:text-base font-semibold hover:bg-primary-dark transition-all whitespace-nowrap cursor-pointer"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {currentStep === 2 && (
              <div className="space-y-5 md:space-y-6">
                <h2 className="text-xl md:text-2xl font-bold text-primary-dark mb-4 md:mb-6">
                  Votre offre
                </h2>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-dark mb-2">
                    Services ou produits proposés *
                  </label>
                  <textarea
                    name="offre"
                    value={formData.offre}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full border-2 border-gray-light rounded-xl px-4 py-2.5 md:py-3 text-sm md:text-base focus:border-primary focus:outline-none transition-colors resize-none"
                    placeholder="Décrivez vos services ou produits..."
                  />
                  <div className="text-xs text-gray-dark mt-1 text-right">
                    {formData.offre.length}/500 caractères
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-dark mb-2">
                    Votre valeur ajoutée *
                  </label>
                  <textarea
                    name="valeurAjoutee"
                    value={formData.valeurAjoutee}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full border-2 border-gray-light rounded-xl px-4 py-2.5 md:py-3 text-sm md:text-base focus:border-primary focus:outline-none transition-colors resize-none"
                    placeholder="Qu'est-ce qui vous différencie ?"
                  />
                  <div className="text-xs text-gray-dark mt-1 text-right">
                    {formData.valeurAjoutee.length}/500 caractères
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="border-2 border-gray-dark text-gray-dark px-6 md:px-8 py-2.5 md:py-3 rounded-xl text-sm md:text-base font-semibold hover:bg-gray-dark hover:text-white transition-all whitespace-nowrap cursor-pointer"
                  >
                    Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(3)}
                    className="bg-primary text-white px-6 md:px-8 py-2.5 md:py-3 rounded-xl text-sm md:text-base font-semibold hover:bg-primary-dark transition-all whitespace-nowrap cursor-pointer"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}

            {/* Step 3 */}
            {currentStep === 3 && (
              <div className="space-y-5 md:space-y-6">
                <h2 className="text-xl md:text-2xl font-bold text-primary-dark mb-4 md:mb-6">
                  Vos besoins
                </h2>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-dark mb-2">
                    Besoins recherchés *
                  </label>
                  <textarea
                    name="besoins"
                    value={formData.besoins}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full border-2 border-gray-light rounded-xl px-4 py-2.5 md:py-3 text-sm md:text-base focus:border-primary focus:outline-none transition-colors resize-none"
                    placeholder="Quels sont vos besoins ? (formation, partenariats, clients, etc.)"
                  />
                  <div className="text-xs text-gray-dark mt-1 text-right">
                    {formData.besoins.length}/500 caractères
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-dark mb-2">
                    Objectifs *
                  </label>
                  <textarea
                    name="objectifs"
                    value={formData.objectifs}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full border-2 border-gray-light rounded-xl px-4 py-2.5 md:py-3 text-sm md:text-base focus:border-primary focus:outline-none transition-colors resize-none"
                    placeholder="Quels sont vos objectifs à court et moyen terme ?"
                  />
                  <div className="text-xs text-gray-dark mt-1 text-right">
                    {formData.objectifs.length}/500 caractères
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="border-2 border-gray-dark text-gray-dark px-6 md:px-8 py-2.5 md:py-3 rounded-xl text-sm md:text-base font-semibold hover:bg-gray-dark hover:text-white transition-all whitespace-nowrap cursor-pointer"
                  >
                    Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(4)}
                    className="bg-primary text-white px-6 md:px-8 py-2.5 md:py-3 rounded-xl text-sm md:text-base font-semibold hover:bg-primary-dark transition-all whitespace-nowrap cursor-pointer"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}

            {/* Step 4 */}
            {currentStep === 4 && (
              <div className="space-y-5 md:space-y-6">
                <h2 className="text-xl md:text-2xl font-bold text-primary-dark mb-4 md:mb-6">
                  Clientèle cible
                </h2>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-dark mb-2">
                    Clientèle visée *
                  </label>
                  <select
                    name="clienteleCible"
                    value={formData.clienteleCible}
                    onChange={handleInputChange}
                    required
                    className="w-full border-2 border-gray-light rounded-xl px-4 py-2.5 md:py-3 text-sm md:text-base focus:border-primary focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Sélectionnez votre cible</option>
                    <option value="particuliers">Particuliers uniquement</option>
                    <option value="professionnels">Professionnels uniquement</option>
                    <option value="les-deux">Particuliers et Professionnels</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-dark mb-2">
                    Zone géographique *
                  </label>
                  <input
                    type="text"
                    name="zoneGeographique"
                    value={formData.zoneGeographique}
                    onChange={handleInputChange}
                    required
                    className="w-full border-2 border-gray-light rounded-xl px-4 py-2.5 md:py-3 text-sm md:text-base focus:border-primary focus:outline-none transition-colors"
                    placeholder="Ville, département, région..."
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-dark mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full border-2 border-gray-light rounded-xl px-4 py-2.5 md:py-3 text-sm md:text-base focus:border-primary focus:outline-none transition-colors"
                    placeholder="votre@email.fr"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-dark mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    required
                    className="w-full border-2 border-gray-light rounded-xl px-4 py-2.5 md:py-3 text-sm md:text-base focus:border-primary focus:outline-none transition-colors"
                    placeholder="06 XX XX XX XX"
                  />
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(3)}
                    className="border-2 border-gray-dark text-gray-dark px-6 md:px-8 py-2.5 md:py-3 rounded-xl text-sm md:text-base font-semibold hover:bg-gray-dark hover:text-white transition-all whitespace-nowrap cursor-pointer"
                  >
                    Précédent
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primary text-white px-8 md:px-12 py-2.5 md:py-3 rounded-xl text-sm md:text-base font-semibold hover:bg-primary-dark transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        Envoyer
                        <i className="ri-send-plane-fill"></i>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-primary/10 border-2 border-primary rounded-xl">
              <div className="flex items-center gap-3 text-primary">
                <i className="ri-checkbox-circle-fill text-xl md:text-2xl"></i>
                <div>
                  <p className="text-sm md:text-base font-semibold">Formulaire envoyé avec succès !</p>
                  <p className="text-xs md:text-sm">Nous vous recontacterons très prochainement.</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border-2 border-red-500 rounded-xl">
              <div className="flex items-center gap-3 text-red-600">
                <i className="ri-error-warning-fill text-xl md:text-2xl"></i>
                <div>
                  <p className="text-sm md:text-base font-semibold">Erreur lors de l'envoi</p>
                  <p className="text-xs md:text-sm">Veuillez réessayer ultérieurement.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FormSection;
